import { useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useWebsiteBlogAll,
  useWebsiteCreateBlogPost,
  useWebsiteUpdateBlogPost,
  useWebsiteUploadBlogInlineImage,
  useMembersOptional,
} from "@/queries/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RichDocumentEditor } from "@/components/ui/rich-document-editor";
import { MemberSelectCombobox } from "@/components/members/MemberSelectCombobox";
import { ImageUploadCropper } from "@/components/ui/image-upload-cropper";
import { PageLoading } from "@/components/layout/PageLoading";
import { NotFoundPage } from "@/components/layout/NotFoundPage";
import { ArrowLeft, ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import { BlogPostStatus, MemberStatus } from "@satyrsmc/shared/client";
import type { BlogPostResponse, Member } from "@satyrsmc/shared/client";
import { toDateOnly } from "@satyrsmc/shared/lib/date-utils";

const FEATURED_IMAGE_ASPECT_RATIO = 1200 / 630;

const EMPTY_BODY = JSON.stringify({ type: "doc", content: [{ type: "paragraph" }] });

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function BlogPostFormPage() {
  const { id } = useParams<{ id: string }>();
  const { data: posts, isLoading } = useWebsiteBlogAll();

  if (id) {
    if (isLoading) return <PageLoading />;
    const post = (posts ?? []).find((p) => p.id === id);
    if (!post) return <NotFoundPage />;
    return <BlogPostForm post={post} />;
  }
  return <BlogPostForm post={null} />;
}

function BlogPostForm({ post }: { post: BlogPostResponse | null }) {
  const navigate = useNavigate();
  const { data: members = [] } = useMembersOptional();
  const createMutation = useWebsiteCreateBlogPost();
  const updateMutation = useWebsiteUpdateBlogPost();
  const uploadInlineImageMutation = useWebsiteUploadBlogInlineImage();

  const [slug, setSlug] = useState(post?.slug ?? "");
  const [slugEdited, setSlugEdited] = useState(post !== null);
  const [title, setTitle] = useState(post?.title ?? "");
  const [excerpt, setExcerpt] = useState(post?.excerpt ?? "");
  const [body, setBody] = useState(post?.body ?? EMPTY_BODY);
  const [status, setStatus] = useState<BlogPostStatus>(post?.status ?? BlogPostStatus.Draft);
  const [publishedAt, setPublishedAt] = useState(() =>
    post ? toDateOnly(post.published_at) : new Date().toISOString().slice(0, 10),
  );
  const [metaTitle, setMetaTitle] = useState(post?.meta_title ?? "");
  const [metaDescription, setMetaDescription] = useState(post?.meta_description ?? "");
  const [authorIds, setAuthorIds] = useState<string[]>(
    post ? [...post.authors].sort((a, b) => a.position - b.position).map((a) => a.member_id) : [],
  );
  const [featuredImageBase64, setFeaturedImageBase64] = useState<string | null | undefined>(
    undefined,
  );
  const [existingFeaturedImageUrl, setExistingFeaturedImageUrl] = useState<string | null>(
    post?.featured_image_url ?? null,
  );
  const [imageAltText, setImageAltText] = useState(post?.image_alt_text ?? "");
  const [featuredCropperOpen, setFeaturedCropperOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const membersById = new Map((members as Member[]).map((m) => [m.id, m]));
  const activeMembers = (members as Member[]).filter(
    (m) => m.status === MemberStatus.Active && !authorIds.includes(m.id),
  );
  const hasFeaturedImage =
    featuredImageBase64 !== null &&
    (featuredImageBase64 !== undefined || existingFeaturedImageUrl !== null);
  const featuredImagePreviewSrc = featuredImageBase64
    ? `data:image/jpeg;base64,${featuredImageBase64}`
    : (existingFeaturedImageUrl ?? undefined);

  function handleTitleChange(value: string) {
    setTitle(value);
    if (!post && !slugEdited) {
      setSlug(slugify(value));
    }
  }

  function addAuthor(memberId: string) {
    setAuthorIds((prev) => [...prev, memberId]);
  }

  function removeAuthor(memberId: string) {
    setAuthorIds((prev) => (prev.length > 1 ? prev.filter((id) => id !== memberId) : prev));
  }

  function moveAuthor(index: number, direction: -1 | 1) {
    setAuthorIds((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  async function handleSave() {
    if (!title.trim() || !slug.trim()) return;
    if (hasFeaturedImage && !imageAltText.trim()) return;
    const payload = {
      slug: slug.trim(),
      title: title.trim(),
      excerpt: excerpt.trim() || null,
      body,
      status,
      published_at: publishedAt || null,
      meta_title: metaTitle.trim() || null,
      meta_description: metaDescription.trim() || null,
      image_alt_text: hasFeaturedImage ? imageAltText.trim() : null,
      ...(authorIds.length > 0 ? { authors: authorIds } : {}),
      ...(featuredImageBase64 !== undefined ? { featured_image: featuredImageBase64 } : {}),
    };
    setSaving(true);
    try {
      if (post) {
        await updateMutation.mutateAsync({ id: post.id, body: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      navigate("/admin/website/blog");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save post.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/website/blog">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{post ? "Edit post" : "Add post"}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {post
              ? "Update this blog post's content, authors, images, and status."
              : "Write a new blog post and publish it to the public site."}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="My New Post"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugEdited(true);
              }}
              placeholder="my-new-post"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as BlogPostStatus)}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BlogPostStatus.Draft}>Draft</SelectItem>
                <SelectItem value={BlogPostStatus.Published}>Published</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="published_at">Post date</Label>
            <DatePicker id="published_at" value={publishedAt} onChange={setPublishedAt} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="excerpt">Excerpt</Label>
            <textarea
              id="excerpt"
              className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Optional short summary"
            />
          </div>
          <div className="grid gap-2">
            <Label>Featured image (optional)</Label>
            {hasFeaturedImage && featuredImagePreviewSrc && (
              <img
                src={featuredImagePreviewSrc}
                alt={imageAltText || "Featured image preview"}
                className="max-w-sm rounded-md border"
              />
            )}
            {featuredImageBase64 && (
              <p className="text-sm text-muted-foreground">
                New featured image selected — will be uploaded on save.
              </p>
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={hasFeaturedImage ? "default" : "outline"}
                onClick={() => setFeaturedCropperOpen(true)}
              >
                {hasFeaturedImage ? "Replace" : "Upload"}
              </Button>
              {hasFeaturedImage && (
                <Button
                  type="button"
                  variant="ghost"
                  aria-label="Remove image"
                  onClick={() => {
                    setFeaturedImageBase64(null);
                    setExistingFeaturedImageUrl(null);
                    setImageAltText("");
                  }}
                >
                  Remove image
                </Button>
              )}
            </div>
            {hasFeaturedImage && (
              <div className="grid gap-2">
                <Label htmlFor="image_alt_text">Image alt text</Label>
                <Input
                  id="image_alt_text"
                  value={imageAltText}
                  onChange={(e) => setImageAltText(e.target.value)}
                  placeholder="Describe the image for screen readers"
                />
              </div>
            )}
          </div>
          <div className="grid gap-2">
            <Label>Body</Label>
            <RichDocumentEditor
              value={body}
              onChange={setBody}
              placeholder="Write your post…"
              stickyToolbar
              fullHeight
              onImageUpload={(base64) =>
                uploadInlineImageMutation.mutateAsync(base64).then((r) => r.url)
              }
            />
          </div>
          <div className="grid gap-2">
            <Label>Authors</Label>
            {authorIds.length > 0 && (
              <ul className="space-y-1">
                {authorIds.map((memberId, index) => (
                  <li
                    key={memberId}
                    className="flex items-center justify-between rounded-md border px-3 py-1.5"
                  >
                    <span className="text-sm">{membersById.get(memberId)?.name ?? memberId}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Move author up"
                        disabled={index === 0}
                        onClick={() => moveAuthor(index, -1)}
                      >
                        <ChevronUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Move author down"
                        disabled={index === authorIds.length - 1}
                        onClick={() => moveAuthor(index, 1)}
                      >
                        <ChevronDown className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Remove author"
                        disabled={authorIds.length <= 1}
                        onClick={() => removeAuthor(memberId)}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <MemberSelectCombobox
              members={activeMembers}
              onSelect={addAuthor}
              placeholder="Search or select a member"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="meta_title">Meta title (SEO)</Label>
            <Input
              id="meta_title"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="meta_description">Meta description (SEO)</Label>
            <textarea
              id="meta_description"
              className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={saving || (hasFeaturedImage && !imageAltText.trim())}
          >
            {saving ? "Saving…" : post ? "Save" : "Create"}
          </Button>
          <Button variant="outline" asChild>
            <Link to="/admin/website/blog">Cancel</Link>
          </Button>
        </div>
        {hasFeaturedImage && !imageAltText.trim() && (
          <p className="text-sm text-muted-foreground">
            Add alt text for the featured image before saving.
          </p>
        )}
      </div>

      <ImageUploadCropper
        open={featuredCropperOpen}
        onClose={() => setFeaturedCropperOpen(false)}
        onConfirm={(base64) => setFeaturedImageBase64(base64)}
        aspectRatio={FEATURED_IMAGE_ASPECT_RATIO}
        title="Featured Image"
        preserveTransparency
      />
    </div>
  );
}
